/**
 * One answer to "which icon does this project use?", shared by every surface.
 *
 * A project's icon is CMMD's record — a Postgres column CMMD's repository sync
 * writes and a person overrides with an emoji. Forge stores none of it: its
 * project rows carry a name and a cwd, so a surface reading them alone falls
 * back to whatever favicon happens to be committed in the checkout. That is
 * wrong for two different projects: a cloud project has no checkout at all, and
 * a LOCAL checkout linked to a CMMD project has one whose contents CMMD's
 * record is supposed to override.
 *
 * So the ladder is:
 *
 *   1. the CMMD project this Forge project is LINKED to (any environment)
 *   2. the CMMD project running on this cloud runtime
 *   3. nothing — let the checkout's own favicon answer
 *
 * Step 1 before step 2 on purpose. Both can answer for a connected cloud
 * project, and they are fed differently: the link path reads the work-intake
 * projects snapshot, which the project Settings tab updates optimistically, so
 * a chosen emoji appears at once. The catalog row is an SSE-fed projection and
 * lags a write by a round trip.
 */

import type { CloudProjectCatalogRow } from "~/cmmd/cmmdForgeRuntimeListResource";
import type { ProjectLinkRow } from "~/cmmd/projectLinkListResource";
import type { ProjectCanonicalIdentity } from "../ProjectIcon";
import {
  resolveSidebarRuntimeId,
  type SavedEnvironmentRuntimeIndex,
} from "../sidebar/sidebarThreadRuntimeIdentity";

/** Runtime id → the CMMD identity of the project running on it. */
export type ProjectIconByRuntimeId = ReadonlyMap<string, ProjectCanonicalIdentity>;
/** `<forgeEnvironmentId>:<forgeProjectId>` → the CMMD identity it is linked to. */
export type ProjectIconByProjectKey = ReadonlyMap<string, ProjectCanonicalIdentity>;

/** A CMMD project record, as the work-intake snapshot carries it. */
export interface CmmdProjectIdentityRow {
  readonly id: string;
  readonly icon?: string | null;
  readonly iconUrl?: string | null;
}

function projectLinkKey(environmentId: string, projectId: string): string {
  return `${environmentId}:${projectId}`;
}

/**
 * Absent, not `{ iconUrl: null, icon: null }`.
 *
 * An empty entry still reads as "CMMD owns this identity" downstream, which
 * suppresses the favicon a local checkout can legitimately resolve from its own
 * cwd. A linked project whose CMMD record simply has no icon yet must fall
 * through to that favicon, not to a letter avatar.
 */
function toCanonicalIdentity(
  row: { readonly icon?: string | null; readonly iconUrl?: string | null } | undefined,
): ProjectCanonicalIdentity | undefined {
  if (row === undefined) return undefined;
  if (!row.iconUrl && !row.icon) return undefined;
  return { iconUrl: row.iconUrl ?? null, icon: row.icon ?? null };
}

/**
 * Runtime id → the CMMD icon for the project running on it.
 *
 * The catalog row is the only place a cloud project's icon exists when no link
 * row covers it — a project registered for Cloud but never linked to a local
 * checkout.
 */
export function buildProjectIconByRuntimeId(
  catalogRows: readonly CloudProjectCatalogRow[],
): ProjectIconByRuntimeId {
  const iconByRuntimeId = new Map<string, ProjectCanonicalIdentity>();
  for (const row of catalogRows) {
    if (row.runtimeId === null) continue;
    const identity = toCanonicalIdentity(row);
    if (identity === undefined) continue;
    iconByRuntimeId.set(row.runtimeId, identity);
  }
  return iconByRuntimeId;
}

/**
 * Forge project → the icon of the CMMD project it is linked to.
 *
 * This is the path that makes a plain local checkout show its real identity.
 * The link table says which CMMD project it is; the CMMD project records say
 * what that project looks like. Neither is inferred from the repository or the
 * display name — those are mutable, and guessing produces a confident wrong
 * icon rather than an honest fallback.
 */
export function buildProjectIconByProjectKey(
  links: readonly ProjectLinkRow[],
  cmmdProjects: readonly CmmdProjectIdentityRow[],
): ProjectIconByProjectKey {
  const identityByCmmdProjectId = new Map<string, ProjectCanonicalIdentity>();
  for (const project of cmmdProjects) {
    const identity = toCanonicalIdentity(project);
    if (identity === undefined) continue;
    identityByCmmdProjectId.set(String(project.id), identity);
  }

  const iconByProjectKey = new Map<string, ProjectCanonicalIdentity>();
  for (const link of links) {
    const identity = identityByCmmdProjectId.get(String(link.cmmdProjectId));
    if (identity === undefined) continue;
    iconByProjectKey.set(projectLinkKey(link.forgeEnvironmentId, link.forgeProjectId), identity);
  }
  return iconByProjectKey;
}

/**
 * The CMMD identity for a project, when CMMD owns one.
 *
 * Returns `undefined` when no CMMD record answers — an unlinked local project,
 * or a linked one whose record carries no icon. Both still have a favicon worth
 * trying, and `ProjectIcon` reads `undefined` as exactly that.
 */
export function resolveProjectCanonicalIcon(
  environmentId: string,
  iconByRuntimeId: ProjectIconByRuntimeId,
  projectId?: string | null | undefined,
  iconByProjectKey?: ProjectIconByProjectKey | undefined,
  /**
   * The saved-environment registry, so a runtime connected under CMMD's
   * durable `env_…` key still resolves to its catalog row. Without it only the
   * `cmmd-runtime:` locator does, and the tooltip drew a letter avatar for a
   * live cloud row (FOR-36687).
   */
  environments: SavedEnvironmentRuntimeIndex = {},
): ProjectCanonicalIdentity | undefined {
  if (projectId && iconByProjectKey) {
    const linked = iconByProjectKey.get(projectLinkKey(environmentId, projectId));
    if (linked !== undefined) return linked;
  }
  const runtimeId = resolveSidebarRuntimeId(environmentId, environments);
  if (runtimeId === null) return undefined;
  return iconByRuntimeId.get(runtimeId);
}
